import { useMutation } from "@tanstack/react-query";

import { useAuthState } from "@/hooks/use-auth-state";
import { useSubaccountsWithPrimary } from "@/hooks/use-subaccounts-with-primary";
import { addressesEqual } from "@/utils/address";

import { useUserOwnedVaultsQuery } from "../queries/vault/user-owned-vaults";
import { useWriteDexSdk } from "../write-dex-sdk-context";
import { useDepositUsdc } from "./deposit-usdc";
import { useDepositIntoVault } from "./vault/deposit-into-vault";

/**
 * Conditionally deposits USDC into the subaccount or vault, depending vault connection status.
 * @returns A mutation function that deposits USDC into the subaccount or vault.
 */
export function useDepositUsdcConditional() {
  const { mutateAsync: depositUsdc } = useDepositUsdc();
  const { mutateAsync: depositIntoVault } = useDepositIntoVault();
  const { connectedVault } = useWriteDexSdk();
  const { accountAddress } = useAuthState();
  const { data: userVaultsData } = useUserOwnedVaultsQuery(accountAddress);
  const { subaccounts } = useSubaccountsWithPrimary();

  return useMutation({
    mutationFn: async ({
      amount,
      toSubaccountAddress,
    }: {
      amount: number;
      toSubaccountAddress?: string;
    }) => {
      // This serves as a check to see if user is depositing into his own owned vault
      const vault = userVaultsData?.items.find((vault) =>
        addressesEqual(vault.subaccount_address, toSubaccountAddress),
      );

      // This serves as a check to see if user is depositing into his own owned vault
      const subaccount = subaccounts.find((subaccount) =>
        addressesEqual(subaccount.subaccount_address, toSubaccountAddress),
      );

      if (vault?.vault_address) {
        await depositIntoVault({
          vaultAddress: vault.vault_address,
          amount,
        });
        return;
      }

      if (connectedVault) {
        await depositIntoVault({
          vaultAddress: connectedVault.vault_address,
          amount,
        });
        return;
      }

      await depositUsdc({ amount, toSubaccountAddress: subaccount?.subaccount_address });
    },
  });
}
